/**
 * Telink 键盘设备上报回填规则集 (Phase 4)
 *
 * 监听 `report:Key_user_data` 事件,把硬件主动上报的高频字段回填到缓存,
 * 让后续 `getXxx()` 调用直接命中缓存,不再触发硬件读。
 *
 * --- 关键约束 ---
 *
 * 1. **字节布局必须与 sendAndWait 真实返回一致**:
 *    回填的 `BackfillItem.data` 布局为 `[ACK_BYTE, CMD_BYTE, LENGTH, 0x00, ...actualData]`,
 *    其中 ACK_BYTE = 0x01 (read_ack_value),这与 `sendAndWait` feature report 响应格式一致。
 *    parser 统一从 offset 4 开始读取有效数据。
 *
 * 2. **长度必须与读取侧完全一致**:
 *    `makeKey(address, length)` 把 length 编进了缓存键,回填长度与 `sendRead` 的
 *    读取长度不一致时缓存**永远命不中**。同时 `data` 至少要有 `4 + length` 字节,
 *    否则命中后 parser 读 `getUint8(4 + i)` 会抛 RangeError。
 *
 * 3. **extract 是纯函数**:
 *    不调用 sendRead / sendWrite / channel.send 等 SDK 方法。
 *
 * --- 为什么不回填灯光参数 ---
 *
 * 灯效的亮度/速度是通过 `getConfig()` 以**整块 8 字节**读出来的
 * (`descriptor.configSize`,键为 `makeKey(baseAddr, 8)`),而 `0x01` 上报只带
 * blMode / blLevel / blSpeed 三个字段,拿不到同一块里的 isColorful / rgb 字节。
 * `extract` 又必须是纯函数、不能补读硬件,因此**无法合成一个正确的配置块**。
 *
 * 强行按单字节地址回填只会写出永远命不中的僵尸条目(读侧长度是 8,回填长度是 1),
 * 并且一旦长度被"对齐"成 8,残缺的载荷会让 parser 越界。
 *
 * 所以灯光参数改为纯失效语义:驱动侧在 blMode / blLevel / blSpeed 变化时调用
 * `cache.invalidateByTag('cfg:N')`,下次读走真实硬件,保证拿到的是完整且自洽的配置块。
 * 详见 `devices/keyboard/telink/device.ts` 的 `dispatchEvent`。
 */

import type { ReportBackfillRule, BackfillItem } from '../../../cache/types'
import { makeKey, addrToNumber } from '../../../cache/CacheKey'
import { ADDRESS_TELINK } from './address'
import { TELINK_KEYBOARD_COMMAND, PROTOCOL_CONFIG } from './keyboard-command'
import { telinkDeviceCustom } from '../../../types/config'
import { getAddress } from '../../../utils/common'

/** ACK 字节 - 与 sendAndWait 真实返回的 ACK 头一致 */
const READ_ACK = PROTOCOL_CONFIG.read_ack_value // 0x01
const READ_EEPROM = TELINK_KEYBOARD_COMMAND.read_eeprom[0] // 0xa4

/**
 * 构造与 sendAndWait 返回格式一致的字节快照。
 *
 * Feature report 响应布局:
 * - Byte 0: ACK (0x01 = 读取成功)
 * - Byte 1: CMD echo (0xa4 = read_eeprom)
 * - Byte 2: length
 * - Byte 3: reserved (0x00)
 * - Byte 4+: actual data bytes
 *
 * 返回的缓冲区长度固定为 `4 + length`,不足的尾部补零,确保命中缓存后
 * parser 读到 `getUint8(4 + length - 1)` 也不会越界。
 *
 * @param actualData 实际数据字节(从 offset 4 开始的部分)
 * @param length 协议层声明的读取长度,必须与读取侧的 sendRead 长度一致
 */
function buildCacheBytes(actualData: number[], length: number): Uint8Array {
  const bytes = new Uint8Array(4 + length)
  bytes[0] = READ_ACK
  bytes[1] = READ_EEPROM
  bytes[2] = length
  bytes[3] = 0x00
  bytes.set(actualData.slice(0, length), 4)
  return bytes
}

/**
 * Telink 键盘上报回填规则集。
 *
 * 文件名含 keyboard 前缀以区分 Telink 底座设备的独立规则集。
 *
 * 当前仅订阅 `report:Key_user_data` 事件,只覆盖:
 * - 当前配置索引 (cfgSel) → CONFIG_CURRENT
 * - 系统模式 (sysMode) → SYS_MODE（仅新协议 FUC_WM 携带该字段时）
 *
 * 灯光参数(blMode / blLevel / blSpeed)不回填,原因见文件头。
 */
export const TELINK_KEYBOARD_REPORT_RULES: ReportBackfillRule<telinkDeviceCustom>[] = [
  {
    event: 'report:Key_user_data',
    extract: (userData: telinkDeviceCustom): BackfillItem[] => {
      const firmwareCfgIdx = userData.cfgSel
      const cfgIdx = userData.cfgSlot ?? firmwareCfgIdx

      // 当前配置索引 (cfgSel)
      // getCurrentConfigIndex() 调用: sendRead(device, getAddress(CONFIG_CURRENT, 0), 8, parser)
      // parser: data.getUint8(4)
      const cfgAddr = getAddress(ADDRESS_TELINK.CONFIG_CURRENT, 0)
      const cfgReadLength = 8
      const items: BackfillItem[] = [
        {
          key: makeKey(cfgAddr, cfgReadLength),
          data: buildCacheBytes([firmwareCfgIdx], cfgReadLength),
          tags: [`cfg:${cfgIdx}`],
          addressStart: addrToNumber(cfgAddr),
          length: cfgReadLength,
        },
      ]

      // getMacMode() 调用: sendRead(device, getAddress(SYS_MODE), 8, data.getUint8(4) === 1)
      // sysMode 本身就是该 8 字节块的有效载荷，可以完整回填；V700 解析结果无该字段则跳过。
      if (userData.sysMode !== undefined) {
        const sysModeAddr = getAddress(ADDRESS_TELINK.SYS_MODE)
        const sysModeReadLength = 8
        items.push({
          key: makeKey(sysModeAddr, sysModeReadLength),
          data: buildCacheBytes([userData.sysMode], sysModeReadLength),
          addressStart: addrToNumber(sysModeAddr),
          length: sysModeReadLength,
        })
      }

      return items
    },
  },
]
